import React, { useState, useContext, useEffect, useRef } from 'react';
import { Col, Row, Form, Card, Modal, Table, Badge, Button, InputGroup, ProgressBar } from 'react-bootstrap';
import { ProductContext } from '../../ProductProvider';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import Preloader from '../../components/Preloader/Preloader';
import { Helmet } from 'react-helmet-async';
import './issuereturn/IssueReturn.css';

const config = require('../../config.json');

export default function IssueBooks() {
  const {
    validateLogin,
    loggedUser,
    handleImageError,
    generateDate,
    generateReturnDate,
    generateUUIDNo
  } = useContext(ProductContext);
  const { enqueueSnackbar } = useSnackbar();

  // Search states
  const [searchBook, setSearchBook] = useState({ type: 'AccessionNo', value: '' });
  const [searchUser, setSearchUser] = useState({ type: 'RegistrationID', value: '' });
  const [searchMode, setSearchMode] = useState('Student'); // 'Student' | 'Faculty'

  // Results & selections
  const [userResult, setUserResult] = useState([]);
  const [userSelect, setUserSelect] = useState(null);
  const [issuedBooks, setIssuedBooks] = useState([]);
  const [bookResult, setBookResult] = useState([]);
  const [bookSelect, setBookSelect] = useState([]);

  // Modals & UI states
  const [loaded, setLoaded] = useState(true);
  const [showIssuedModal, setShowIssuedModal] = useState(false);
  const [userListShow, setUserListShow] = useState(false);
  const [bookListShow, setBookListShow] = useState(false);

  const refBook = useRef([]);
  const refUser = useRef([]);

  useEffect(() => {
    if (!loggedUser.isAuthenticated) {
      validateLogin();
    }
  }, [loggedUser]);

  // Calculate Department Quotas & Loan Days
  const getBorrowerRules = (user) => {
    if (!user) return { maxLimit: 20, loanDays: 30, dept: 'General', isStaff: false };
    const reg = (user.registrationNo || user.userID || '').toUpperCase();
    const deptCode = reg.slice(2, 5);

    if (reg.startsWith('VJS') || (user.userName && !user.applicantName) || searchMode === 'Faculty') {
      return { maxLimit: 50, loanDays: 90, dept: 'Staff / Faculty', isStaff: true };
    }
    if (deptCode === 'LTH') {
      return { maxLimit: 30, loanDays: 40, dept: 'Licentiate in Theology (LTH)', isStaff: false };
    }
    if (deptCode === 'DTH') {
      return { maxLimit: 50, loanDays: 60, dept: 'Doctorate in Theology (DTH)', isStaff: false };
    }
    if (deptCode === 'OYT') {
      return { maxLimit: 20, loanDays: 30, dept: 'One Year Theology (OYT)', isStaff: false };
    }
    if (deptCode === 'BTH') {
      return { maxLimit: 20, loanDays: 30, dept: 'Bachelor in Theology (BTH)', isStaff: false };
    }
    return { maxLimit: 20, loanDays: 30, dept: deptCode || 'Student', isStaff: false };
  };

  // Helper to determine loan days for a specific book and borrower
  const getBookLoanDays = (book, rules) => {
    const isReference = (book?.bookScope || '').toLowerCase() === 'reference';
    if (isReference) {
      return rules?.isStaff ? 90 : 1;
    }
    return rules?.loanDays || 30;
  };

  // Search User
  const handleUserSearch = async (event) => {
    if (event) event.preventDefault();
    const query = searchUser.value.trim();
    //console.log("option :", searchUser.type, "mode :", searchMode);
    if (!query) {
      enqueueSnackbar('Please enter a Registration ID or Name to search borrower', { variant: 'info' });
      return;
    }
    if (searchUser.type === 'UserName' && query.length < 3) {
      enqueueSnackbar('For Name search, please enter at least 3 characters', { variant: 'warning' });
      return;
    }

    try {
      setLoaded(false);
      setUserSelect(null);
      setIssuedBooks([]);

      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      let searchUrl = '';
      let mode = 'Student';

      if (searchUser.type === 'UserName') {
        mode = searchMode;
        if (mode === 'Faculty') {
          const clientID = loggedUser.website || 'VJCollege';
          const fetchOption = "LibraryUserName";
          searchUrl = `${config.userapi.invokeURL}/dashboard/officeform?fetchKey=${query}&fetchOption=${fetchOption}&clientID=${clientID}`;
        } else {
          searchUrl = `${config.userapi.invokeURL}/dashboard/student/search?fetchKey=${query}&fetchOption=${searchUser.type}`;
        }
      } else {
        if (query.toUpperCase().startsWith('VJS')) {
          mode = 'Faculty';
          const clientID = loggedUser.website || 'VJCollege';
          //const fetchOption = "UserID";
          searchUrl = `${config.userapi.invokeURL}/dashboard/officeform?fetchKey=${query}&fetchOption=${searchUser.type}&clientID=${clientID}`;
        } else {
          mode = 'Student';
          searchUrl = `${config.userapi.invokeURL}/dashboard/student/search?fetchKey=${query}&fetchOption=${searchUser.type}`;
        }
        setSearchMode(mode);
      }

      const response = await axios.get(searchUrl, {
        headers: {
          Authorization: idToken,
          'x-api-key': config.userapi.key
        }
      });

      const data = Array.isArray(response.data) ? response.data : [];

      if (data.length === 1) {
        const item = data[0];
        const normalized = mode === 'Faculty' ? {
          ...item,
          registrationNo: item.userID,
          applicantName: item.userName,
          emailID: item.userEmailID,
          avatar: item.userID,
          applicationNo: item.userID
        } : item;

        setUserResult([normalized]);
        setUserSelect(normalized);
        await getIssuedBooks(normalized.registrationNo || normalized.userID);
        enqueueSnackbar(`Borrower '${normalized.applicantName || normalized.userName}' identified`, { variant: 'success' });
      } else if (data.length > 1) {
        setUserResult(data);
        setUserListShow(true);
      } else {
        setUserResult([]);
        enqueueSnackbar(`No ${mode.toLowerCase()} record found for '${query}'`, { variant: 'info' });
      }
      setLoaded(true);
    } catch (error) {
      console.error('Error searching borrower:', error);
      enqueueSnackbar('Error finding borrower: ' + (error.response?.data?.message || error.message), { variant: 'warning' });
      setLoaded(true);
    }
  };

  // Get Currently Issued Books for User
  const getIssuedBooks = async (userId) => {
    try {
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const response = await axios.get(`${config.api.invokeURL}/vjlibrary/issue`, {
        params: {
          fetchKey: userId,
          fetchOption: 'UserID',
          clientID: 'VJCollege'
        },
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });
      const data = Array.isArray(response.data) ? response.data : [];
      setIssuedBooks(data);
    } catch (error) {
      console.error('Error getting issued books:', error);
    }
  };

  // Search Book to Issue
  const handleBookSearch = async (event) => {
    if (event) event.preventDefault();
    const query = searchBook.value.trim();
    if (!query) {
      enqueueSnackbar('Please enter an Accession # / Barcode or Book Title', { variant: 'info' });
      return;
    }

    try {
      setLoaded(false);
      const { idToken } = (await fetchAuthSession()).tokens ?? {};

      const response = await axios.get(`${config.api.invokeURL}/vjlibrary/search/books`, {
        params: {
          fetchKey: query.toUpperCase(),
          fetchOption: searchBook.type,
          clientID: 'VJCollege'
        },
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      const data = Array.isArray(response.data) ? response.data : [];

      if (data.length === 1) {
        const book = data[0];
        stageBook(book);
      } else if (data.length > 1) {
        setBookResult(data);
        setBookListShow(true);
      } else {
        enqueueSnackbar('No books found with this search term', { variant: 'info' });
      }
      setLoaded(true);
    } catch (error) {
      console.error('Error searching book:', error);
      enqueueSnackbar('Error searching book: ' + (error.response?.data?.message || error.message), { variant: 'warning' });
      setLoaded(true);
    }
  };

  // Stage a Book for Issue
  const stageBook = (book) => {
    if (!book) return;

    if (book.bookStatus !== 'Active' && book.bookStatus !== 'Available') {
      enqueueSnackbar(`Book #${book.accessionNo} (${book.bookTitle}) is currently marked as '${book.bookStatus || 'Issued'}'`, { variant: 'warning' });
      return;
    }

    if (bookSelect.some((b) => b.accessionNo === book.accessionNo)) {
      enqueueSnackbar(`Book #${book.accessionNo} is already in the staging list`, { variant: 'info' });
      return;
    }

    const isRef = (book.bookScope || '').toLowerCase() === 'reference';
    const tbookingid = generateUUIDNo ? generateUUIDNo() : 'BK-' + Date.now();
    const staged = {
      ...book,
      issueCount: 1,
      issueID: 'BK-' + tbookingid
    };

    setBookSelect((prev) => [...prev, staged]);
    if (isRef) {
      enqueueSnackbar(`Added Reference Book '${book.bookTitle}' (#${book.accessionNo}) [1-day loan for students / 90-day for staff]`, { variant: 'info' });
    } else {
      enqueueSnackbar(`Added '${book.bookTitle}' (#${book.accessionNo}) to staging list`, { variant: 'success' });
    }
    setSearchBook((prev) => ({ ...prev, value: '' }));
  };

  // Remove Staged Book
  const removeStagedBook = (accNo) => {
    setBookSelect((prev) => prev.filter((b) => b.accessionNo !== accNo));
  };

  // Select User from Multi-User Modal
  const handleSelectUserFromModal = (user) => {
    const normalized = searchMode === 'Faculty' ? {
      ...user,
      registrationNo: user.userID,
      applicantName: user.userName,
      emailID: user.userEmailID,
      avatar: user.userID,
      applicationNo: user.userID
    } : user;

    setUserSelect(normalized);
    setUserListShow(false);
    getIssuedBooks(normalized.registrationNo || normalized.userID);
    enqueueSnackbar(`Selected borrower: ${normalized.applicantName || normalized.userName}`, { variant: 'success' });
  };

  // Issue Books Execution
  const handleBookIssue = async (event) => {
    if (event) event.preventDefault();

    if (!userSelect) {
      enqueueSnackbar('Please select a borrower first', { variant: 'info' });
      return;
    }

    if (!bookSelect || bookSelect.length === 0) {
      enqueueSnackbar('Please search and select at least one book to issue', { variant: 'info' });
      return;
    }

    const rules = getBorrowerRules(userSelect);
    const currentIssuedCount = issuedBooks.length;
    const toIssueCount = bookSelect.length;
    const willTotal = currentIssuedCount + toIssueCount;

    if (rules.maxLimit !== Number.POSITIVE_INFINITY && willTotal > rules.maxLimit) {
      enqueueSnackbar(
        `Issue limit reached for ${rules.dept}: max ${rules.maxLimit} books. (${currentIssuedCount} currently active + ${toIssueCount} new exceeds quota).`,
        { variant: 'warning' }
      );
      return;
    }

    try {
      setLoaded(false);
      const todayDate = await generateDate();

      // Calculate loanDays and bookedTo for each staged book
      const processedBookSelect = await Promise.all(
        bookSelect.map(async (book) => {
          const bookLoanDays = getBookLoanDays(book, rules);
          const bookReturnDate = await generateReturnDate(bookLoanDays);
          return {
            ...book,
            loanDays: bookLoanDays,
            bookedFrom: todayDate,
            bookedTo: bookReturnDate
          };
        })
      );

      // Primary batch return date (earliest return date among items)
      const minDueDays = Math.min(...processedBookSelect.map((b) => b.loanDays));
      const primaryReturnDate = await generateReturnDate(minDueDays);

      const { idToken } = (await fetchAuthSession()).tokens ?? {};

      const params = {
        clientID: loggedUser.website || 'VJCollege',
        bookSelect: processedBookSelect,
        bookStatus: 'Issued',
        bookedOn: todayDate,
        bookedfor: userSelect.applicantName || userSelect.userName,
        userID: userSelect.registrationNo || userSelect.userID,
        userEmailID: userSelect.emailID || userSelect.userEmailID,
        bookedFrom: todayDate,
        bookedTo: primaryReturnDate,
        updatedBy: loggedUser.nickname || loggedUser.userName || 'Librarian',
        updatedOn: todayDate
      };

      await axios.post(`${config.api.invokeURL}/vjlibrary/issue`, params, {
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      // Send Email Notification
      if (userSelect.emailID || userSelect.userEmailID) {
        const emailParams = {
          bookSelect: processedBookSelect,
          bookedfor: userSelect.applicantName || userSelect.userName,
          bookedFrom: todayDate,
          bookedTo: primaryReturnDate,
          userEmailID: [userSelect.emailID || userSelect.userEmailID]
        };
        axios.post(`${config.api.invokeURL}/vjlibrary/notification`, emailParams, {
          headers: {
            Authorization: idToken,
            'x-api-key': config.api.key
          }
        }).catch((e) => console.error('Notification error:', e));
      }

      const count = processedBookSelect.length;
      const refCount = processedBookSelect.filter((b) => (b.bookScope || '').toLowerCase() === 'reference').length;
      let successMsg = `Successfully issued ${count} book${count === 1 ? '' : 's'} to ${userSelect.applicantName || userSelect.userName}!`;
      if (refCount > 0 && !rules.isStaff) {
        successMsg += ` (${refCount} Reference item on 1-day loan - Due: ${primaryReturnDate})`;
      } else {
        successMsg += ` Due: ${primaryReturnDate}`;
      }

      enqueueSnackbar(successMsg, { variant: 'success' });

      // Refresh borrower's loan list and clear staged books
      await getIssuedBooks(userSelect.registrationNo || userSelect.userID);
      setBookSelect([]);
      setSearchBook({ type: 'AccessionNo', value: '' });
      setLoaded(true);
    } catch (error) {
      console.error('Error issuing books:', error);
      enqueueSnackbar('Error issuing books: ' + (error.response?.data?.message || error.message), { variant: 'warning' });
      setLoaded(true);
    }
  };

  // Reset entire transaction
  const handleFullReset = () => {
    setUserSelect(null);
    setUserResult([]);
    setIssuedBooks([]);
    setBookSelect([]);
    setBookResult([]);
    setSearchUser({ type: 'RegistrationID', value: '' });
    setSearchBook({ type: 'AccessionNo', value: '' });
    setSearchMode('Student');
    enqueueSnackbar('Circulation session reset for next borrower', { variant: 'info' });
  };

  const borrowerRules = userSelect ? getBorrowerRules(userSelect) : null;
  const quotaPercentage = borrowerRules && borrowerRules.maxLimit !== Number.POSITIVE_INFINITY
    ? Math.min(100, Math.round(((issuedBooks.length + bookSelect.length) / borrowerRules.maxLimit) * 100))
    : 0;

  return (
    <div className="container-fluid px-0">
      <Helmet>
        <title>Issue Books - Circulation Desk | Flynn - Meagher Library</title>
      </Helmet>

      {!loaded && <Preloader show={true} />}

      <Row className="g-4">
        {/* Left Column: Borrower Identification */}
        <Col lg={5} md={12}>
          <div className="circulation-card">
            <div className="circulation-card-header">
              <h5 className="circulation-card-title">
                <i className="bi bi-person-badge text-primary"></i> 1. Identify Borrower
              </h5>
              <div className="d-flex align-items-center gap-2">
                {searchUser.type === 'UserName' && (
                  <div className="borrower-mode-toggle" role="group" aria-label="Borrower Category">
                    <button
                      type="button"
                      className={`borrower-mode-btn ${searchMode === 'Student' ? 'active' : ''}`}
                      onClick={() => setSearchMode('Student')}
                      title="Search Students"
                    >
                      <i className="bi bi-person-fill me-1"></i> Student
                    </button>
                    <button
                      type="button"
                      className={`borrower-mode-btn ${searchMode === 'Faculty' ? 'active' : ''}`}
                      onClick={() => setSearchMode('Faculty')}
                      title="Search Faculty / Staff"
                    >
                      <i className="bi bi-mortarboard-fill me-1"></i> Faculty
                    </button>
                  </div>
                )}
                {userSelect && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary py-0 px-2 small"
                    onClick={() => {
                      setUserSelect(null);
                      setIssuedBooks([]);
                    }}
                    title="Change borrower"
                  >
                    <i className="bi bi-arrow-repeat me-1"></i> Change
                  </button>
                )}
              </div>
            </div>

            <div className="circulation-card-body">
              {/* Borrower Search Form */}
              <Form onSubmit={handleUserSearch}>
                <Row className="g-2">
                  <Col sm={4}>
                    <Form.Select
                      size="sm"
                      className="circulation-input w-100"
                      value={searchUser.type}
                      onChange={(e) => {
                        const newType = e.target.value;
                        setSearchUser({ ...searchUser, type: newType });
                        if (newType === 'RegistrationID') {
                          setSearchMode('Student');
                        }
                      }}
                    >
                      <option value="RegistrationID">User ID</option>
                      <option value="UserName">Name</option>
                    </Form.Select>
                  </Col>
                  <Col sm={8}>
                    <InputGroup size="sm">
                      <Form.Control
                        type="text"
                        className="circulation-input"
                        placeholder={
                          searchUser.type === 'UserName'
                            ? searchMode === 'Faculty'
                              ? 'Enter Faculty Name...'
                              : 'Enter Student Name...'
                            : 'Scan card or enter ID...'
                        }
                        value={searchUser.value}
                        onChange={(e) => setSearchUser({ ...searchUser, value: e.target.value.toUpperCase() })}
                      />
                      <Button type="submit" variant="primary" className="px-3 fw-bold">
                        <i className="bi bi-search"></i>
                      </Button>
                    </InputGroup>
                  </Col>
                </Row>
              </Form>

              {/* Identified Borrower Profile Card */}
              {userSelect ? (
                <div className="borrower-profile-box mt-3">
                  <div className="d-flex align-items-center gap-3 mb-2">
                    <img
                      src={
                        searchMode === 'Student'
                          ? `${config.s3.profileUrl}${userSelect.applicationNo}/${userSelect.avatar}`
                          : `${config.s3.imageURL}avatars/admins/${userSelect.userID}.jpeg`
                      }
                      onError={handleImageError}
                      alt="Borrower Photo"
                      className="borrower-avatar"
                    />
                    <div className="flex-grow-1 overflow-hidden">
                      <div className="fw-bold text-dark fs-6 text-truncate">
                        {userSelect.applicantName || userSelect.userName}
                      </div>
                      <div className="small text-muted font-monospace">
                        ID: <strong>{userSelect.registrationNo || userSelect.userID}</strong>
                      </div>
                      <Badge bg="primary" className="mt-1">
                        {borrowerRules?.dept}
                      </Badge>
                    </div>
                  </div>

                  {/* Quota & Active Loans Summary */}
                  <div className="border-top pt-2 mt-2">
                    <div className="d-flex justify-content-between align-items-center small mb-1">
                      <span className="text-muted fw-semibold">Borrowing Quota:</span>
                      <span className="fw-bold text-dark">
                        {issuedBooks.length + bookSelect.length} / {borrowerRules?.maxLimit || '∞'} Books
                      </span>
                    </div>
                    <ProgressBar
                      now={quotaPercentage}
                      variant={quotaPercentage >= 100 ? 'danger' : quotaPercentage >= 75 ? 'warning' : 'info'}
                      style={{ height: '7px' }}
                    />
                    <div className="d-flex justify-content-between align-items-center mt-2 small text-muted">
                      <span>Loan Period: <strong>{borrowerRules?.loanDays} Days</strong></span>
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-0 text-decoration-none fw-bold"
                        onClick={() => setShowIssuedModal(true)}
                      >
                        <i className="bi bi-journal-check me-1"></i>
                        {issuedBooks.length} Currently Issued
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 text-muted small border rounded-3 mt-3 bg-light">
                  <i className="bi bi-person-bounding-box fs-2 text-secondary d-block mb-1"></i>
                  Scan student card or enter registration ID above to identify borrower.
                </div>
              )}
            </div>
          </div>
        </Col>

        {/* Right Column: Book Selection & Staging */}
        <Col lg={7} md={12}>
          <div className="circulation-card">
            <div className="circulation-card-header">
              <h5 className="circulation-card-title">
                <i className="bi bi-book-half text-primary"></i> 2. Stage Books for Checkout
              </h5>
              {bookSelect.length > 0 && (
                <Badge bg="success" className="px-2 py-1">
                  {bookSelect.length} Book{bookSelect.length === 1 ? '' : 's'} Selected
                </Badge>
              )}
            </div>

            <div className="circulation-card-body">
              {/* Book Search Form */}
              <Form onSubmit={handleBookSearch}>
                <Row className="g-2 align-items-center">
                  <Col sm={4}>
                    <Form.Select
                      size="sm"
                      className="circulation-input w-100"
                      value={searchBook.type}
                      onChange={(e) => setSearchBook({ ...searchBook, type: e.target.value })}
                    >
                      <option value="AccessionNo">Accession # / Barcode</option>
                      <option value="BookTitle">Book Title</option>
                      <option value="BookAuthor">Author Name</option>
                    </Form.Select>
                  </Col>
                  <Col sm={8}>
                    <InputGroup size="sm">
                      <Form.Control
                        type="text"
                        className="circulation-input"
                        placeholder="Scan barcode or enter book details..."
                        value={searchBook.value}
                        onChange={(e) => setSearchBook({ ...searchBook, value: e.target.value })}
                      />
                      <Button type="submit" variant="primary" className="px-3 fw-bold">
                        <i className="bi bi-plus-lg me-1"></i> Add Book
                      </Button>
                    </InputGroup>
                  </Col>
                </Row>
              </Form>

              {/* Staged Books List */}
              <div className="mt-3">
                <h6 className="small fw-bold text-muted text-uppercase mb-2">
                  Staged Books to be Issued ({bookSelect.length})
                </h6>

                {bookSelect.length === 0 ? (
                  <div className="text-center py-4 text-muted small border rounded-3 bg-light">
                    <i className="bi bi-upc-scan fs-2 text-secondary d-block mb-1"></i>
                    Scan book barcodes or search by accession number to stage items for issue.
                  </div>
                ) : (
                  <div className="border rounded-3 overflow-hidden">
                    <Table hover responsive className="mb-0 align-middle">
                      <thead className="bg-light small fw-bold text-muted">
                        <tr>
                          <th>#</th>
                          <th>Accession #</th>
                          <th>Book Title</th>
                          <th>Author</th>
                          <th>Scope / Loan</th>
                          <th>Call No</th>
                          <th className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="small">
                        {bookSelect.map((book, idx) => {
                          const isRef = (book.bookScope || '').toLowerCase() === 'reference';
                          const loanDays = getBookLoanDays(book, borrowerRules);

                          return (
                            <tr key={book.accessionNo || idx}>
                              <td className="fw-bold text-muted">{idx + 1}</td>
                              <td>
                                <span className="badge bg-light text-primary border font-monospace">
                                  #{book.accessionNo}
                                </span>
                              </td>
                              <td>
                                <div className="fw-bold text-dark">{book.bookTitle}</div>
                                <span className="badge bg-success bg-opacity-10 text-success" style={{ fontSize: '0.72rem' }}>
                                  Available for Issue
                                </span>
                              </td>
                              <td className="text-muted">{book.bookAuthor || 'Unknown'}</td>
                              <td>
                                {isRef ? (
                                  <div className="d-flex flex-column gap-1 align-items-start">
                                    <Badge bg="warning" text="dark" style={{ fontSize: '0.72rem' }}>
                                      <i className="bi bi-bookmark-star me-1"></i> Reference
                                    </Badge>
                                    <span className="badge bg-danger bg-opacity-10 text-danger border border-danger-subtle font-monospace" style={{ fontSize: '0.7rem' }}>
                                      {borrowerRules?.isStaff ? '90-Day Staff Loan' : '1-Day Student Loan'}
                                    </span>
                                  </div>
                                ) : (
                                  <div className="d-flex flex-column gap-1 align-items-start">
                                    <Badge bg="light" text="secondary" className="border" style={{ fontSize: '0.72rem' }}>
                                      Circulation
                                    </Badge>
                                    <span className="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle font-monospace" style={{ fontSize: '0.7rem' }}>
                                      {borrowerRules ? `${loanDays}-Day Loan` : 'Standard Loan'}
                                    </span>
                                  </div>
                                )}
                              </td>
                              <td>
                                <span className="badge bg-light text-dark border font-monospace">
                                  {book.bookClassificationNo || 'N/A'}
                                </span>
                              </td>
                              <td className="text-center">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger py-0 px-2"
                                  onClick={() => removeStagedBook(book.accessionNo)}
                                  title="Remove from issue list"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="d-flex flex-wrap justify-content-between align-items-center pt-3 border-top mt-4 gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleFullReset}
                >
                  <i className="bi bi-arrow-counterclockwise me-1"></i> Reset Transaction
                </Button>

                <button
                  type="button"
                  className="btn-circulation-success"
                  onClick={handleBookIssue}
                  disabled={!userSelect || bookSelect.length === 0}
                >
                  <i className="bi bi-check2-circle fs-5"></i>
                  Confirm & Issue {bookSelect.length > 0 ? `(${bookSelect.length} Books)` : ''}
                </button>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Currently Issued Books Modal */}
      <Modal show={showIssuedModal} onHide={() => setShowIssuedModal(false)} size="lg" centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fs-6 fw-bold text-dark">
            <i className="bi bi-journal-text text-primary me-2"></i>
            Active Loans for {userSelect?.applicantName || userSelect?.userName} ({issuedBooks.length})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {issuedBooks.length > 0 ? (
            <Table hover responsive className="mb-0 align-middle small">
              <thead className="bg-light fw-bold text-muted">
                <tr>
                  <th>#</th>
                  <th>Accession #</th>
                  <th>Book Title</th>
                  <th>Issued Date</th>
                  <th>Due Date</th>
                </tr>
              </thead>
              <tbody>
                {issuedBooks.map((item, index) => (
                  <tr key={index}>
                    <td className="fw-bold text-muted">{index + 1}</td>
                    <td>
                      <span className="badge bg-light text-primary border font-monospace">
                        #{item.accessionNo}
                      </span>
                    </td>
                    <td className="fw-semibold text-dark">
                      {item.bookTitle}
                      {((item.bookScope || '').toLowerCase() === 'reference' || (item.scope || '').toLowerCase() === 'reference') && (
                        <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '0.68rem' }}>
                          Reference
                        </Badge>
                      )}
                    </td>
                    <td className="text-muted">{item.bookedOn || 'N/A'}</td>
                    <td>
                      <span className="badge bg-info text-dark">
                        {item.bookedTo || 'N/A'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-4 text-muted">No currently active loans for this borrower.</div>
          )}
        </Modal.Body>
      </Modal>

      {/* User Selection Modal (When multiple matches found) */}
      <Modal show={userListShow} onHide={() => setUserListShow(false)} size="lg" centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fs-6 fw-bold text-dark">
            <i className="bi bi-people text-primary me-2"></i> Select {searchMode === 'Faculty' ? 'Faculty / Staff' : 'Student'} ({userResult.length} matches found)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <Table hover responsive className="mb-0 align-middle small">
            <thead className="bg-light fw-bold text-muted">
              <tr>
                <th>{searchMode === 'Faculty' ? 'Staff ID' : 'Registration ID'}</th>
                <th>Borrower Name</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {userResult.map((item, idx) => (
                <tr key={idx}>
                  <td>
                    <span className="badge bg-light text-primary border font-monospace">
                      #{item.registrationNo || item.userID}
                    </span>
                  </td>
                  <td>
                    <div className="fw-bold text-dark">{item.applicantName || item.userName}</div>
                    <div className="text-muted small">{item.emailID || item.userEmailID}</div>
                  </td>
                  <td className="text-center">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => handleSelectUserFromModal(item)}
                    >
                      Select
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>

      {/* Book Multi-Select Modal */}
      <Modal show={bookListShow} onHide={() => setBookListShow(false)} size="lg" centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fs-6 fw-bold text-dark">
            <i className="bi bi-journals text-primary me-2"></i> Select Book ({bookResult.length} matches found)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <Table hover responsive className="mb-0 align-middle small">
            <thead className="bg-light fw-bold text-muted">
              <tr>
                <th>Accession #</th>
                <th>Book Title</th>
                <th>Author</th>
                <th>Scope</th>
                <th>Status</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {bookResult.map((item, idx) => {
                const isSelected = bookSelect.some((b) => b.accessionNo === item.accessionNo);
                const isRef = (item.bookScope || '').toLowerCase() === 'reference';
                return (
                  <tr key={idx}>
                    <td>
                      <span className="badge bg-light text-primary border font-monospace">
                        #{item.accessionNo}
                      </span>
                    </td>
                    <td className="fw-semibold text-dark">{item.bookTitle}</td>
                    <td className="text-muted">{item.bookAuthor || 'Unknown'}</td>
                    <td>
                      {isRef ? (
                        <Badge bg="warning" text="dark" style={{ fontSize: '0.72rem' }}>
                          <i className="bi bi-bookmark-star me-1"></i> Reference
                        </Badge>
                      ) : (
                        <Badge bg="light" text="secondary" className="border" style={{ fontSize: '0.72rem' }}>
                          Circulation
                        </Badge>
                      )}
                    </td>
                    <td>
                      <Badge bg={item.bookStatus === 'Active' || item.bookStatus === 'Available' ? 'success' : 'secondary'}>
                        {item.bookStatus || 'Available'}
                      </Badge>
                    </td>
                    <td className="text-center">
                      <Button
                        size="sm"
                        variant={isSelected ? 'outline-danger' : 'primary'}
                        onClick={() => {
                          if (isSelected) {
                            removeStagedBook(item.accessionNo);
                          } else {
                            stageBook(item);
                          }
                        }}
                      >
                        {isSelected ? 'Remove' : 'Add to Issue'}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>
    </div>
  );
}
